import { CommonModule } from '@angular/common';
import { Component, ViewChild, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AlertComponent, ButtonComponent, MbsPopupType, MbsSize, ModalComponent, ModalFooterDirective, TagComponent } from 'mbs-ui-kit';
import { ApplicationStateFacade } from 'mbs-ui-shared@facades/application.facade';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { UiStorageKey } from 'mbs-ui-shared@models';
import { onlyDateFormat } from 'mbs-ui-shared@utils/date';
import moment from 'moment';

import { I18NextPipe } from 'angular-i18next';
import { Observable, catchError, filter, finalize, map, of, switchMap, take, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'mbs-allow-offline-edit-modal',
  styleUrls: ['./allow-offline-edit-modal.component.scss'],
  templateUrl: './allow-offline-edit-modal.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    AlertComponent,
    ModalComponent,
    TagComponent,
    I18NextPipe,
    ModalFooterDirective
  ]
})
export class AllowOfflineEditModalComponent {
  private applicationStateFacade = inject(ApplicationStateFacade);
  private auth = inject(AuthFacade);

  public readonly MbsSize = MbsSize;
  public readonly alertType = MbsPopupType;

  public readonly elementsSelector = {
    name: {
      simpleModeTitle: 'simpleModeTitle',
      notSimpleModeTitle: 'notSimpleModeTitle',
      notSimpleModeContent: 'notSimpleModeContent',
      simpleModeButtons: 'simpleModeButtons',
      notSimpleModeButtons: 'notSimpleModeButtons'
    }
  };

  public loading = false;

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  tryLaterClickHandler(): void {
    (!this.baseModal?.data?.simpleMode ? this.updateStorageData(true) : of(null))
      .pipe(
        tap(() => localStorage.removeItem('isOpenAllowOfflineEdit')),
        switchMap(() => this.auth.fetchCurrentUser$),
        take(1),
        untilDestroyed(this)
      )
      .subscribe(() => this.baseModal.close());
  }

  allowClickHandler(): void {
    this.loading = true;

    this.auth
      .allowOfflineEdit()
      .pipe(
        take(1),
        switchMap(() => this.updateStorageData()),
        map(() => true),
        catchError(() => of(false)),
        filter(Boolean),
        finalize(() => (this.loading = false)),
        untilDestroyed(this)
      )
      .subscribe(() => {
        localStorage.removeItem('isOpenAllowOfflineEdit');
        this.baseModal.save();
      });
  }

  private updateStorageData(needUpdate = false): Observable<null> {
    const allowEdit = this.baseModal.data.allowOfflineEdit || {};

    if (needUpdate) {
      allowEdit[this.baseModal.data.id] = {
        showed: (allowEdit[this.baseModal.data.id]?.showed || 0) + 1,
        lastShow: moment().format(onlyDateFormat)
      };

      return this.applicationStateFacade.updateStorageSetting(UiStorageKey.AllowOfflineEdit, JSON.stringify(allowEdit));
    }

    const newData = Object.keys(allowEdit).reduce((acc, next) => {
      if (next !== this.baseModal.data.id) acc[next] = this.baseModal.data.allowOfflineEdit[next];

      return acc;
    }, {});

    return this.applicationStateFacade.updateStorageSetting(UiStorageKey.AllowOfflineEdit, JSON.stringify(newData));
  }
}
